import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, PlusCircle, Users, LogOut, Stethoscope, UserCircle, UsersRound, ScrollText, X,
  CalendarDays, Mail, Building2, Home, BedDouble, ReceiptText, Sparkles, BarChart3, Tag, ClipboardList,
  KeyRound, Wallet, Settings2, ClipboardCheck, TrendingUp, ShieldCheck, Receipt, HardHat, Contact,
  ShieldAlert, House, Car, UserPlus, Handshake, Workflow, ListChecks, ChevronDown,
} from "lucide-react";

// Items keep their perm gating; they are now organised into collapsible groups.
const TOP_ITEMS = [
  { to: "/organizations", icon: Building2,       label: "Organizations", globalAdminOnly: true },
  { to: "/dashboard",     icon: LayoutDashboard, label: "Dashboard" },
];

const NAV_GROUPS = [
  {
    key: "tenant", label: "My Tenancy", tenantOnly: true,
    items: [
      { to: "/my/lease",    icon: House,       label: "My Lease",    perm: "my_lease.view" },
      { to: "/my/invoices", icon: ReceiptText, label: "My Invoices", perm: "my_invoices.view" },
      { to: "/my/payments", icon: Wallet,      label: "My Payments", perm: "my_payments.view" },
    ],
  },
  {
    key: "visitors", label: "Visitors & Requests",
    items: [
      { to: "/calendar",         icon: CalendarDays, label: "Visitor Calendar", perm: "calendar.view" },
      { to: "/requests/new",     icon: PlusCircle,   label: "New Request",      perm: "requests.create" },
      { to: "/visitors",         icon: UsersRound,   label: "Visitors",         perm: "visitors.view" },
      { to: "/guest-requests",   icon: UserPlus,     label: "Guest Requests",   perm: "guest_requests.create" },
      { to: "/medical-licenses", icon: ShieldCheck,  label: "Medical Licenses", perm: "medical_licenses.view" },
      { to: "/sponsor-orgs",     icon: Handshake,    label: "Sponsor Orgs",     perm: "sponsor_orgs.view" },
    ],
  },
  {
    key: "housing", label: "Housing & Stays",
    items: [
      { to: "/bookings",         icon: BedDouble,    label: "Bookings",         perm: "bookings.view" },
      { to: "/housing-calendar", icon: CalendarDays, label: "Housing Calendar", perm: "bookings.view" },
      { to: "/housing-units",    icon: Home,         label: "Housing Units",    perm: "housing_units.manage" },
      { to: "/leases",           icon: KeyRound,     label: "Leases",           perm: "leases.view" },
      { to: "/rate-cards",       icon: Tag,          label: "Rate Cards",       perm: "rate_cards.manage" },
      { to: "/transport",        icon: Car,          label: "Transport",        perm: "bookings.view" },
    ],
  },
  {
    key: "operations", label: "Operations",
    items: [
      { to: "/cleaners",         icon: Sparkles,       label: "Operations Staff",    perm: "cleaners.manage" },
      { to: "/helpers-calendar", icon: Users,          label: "Operations Calendar", perm: "helpers.assign" },
      { to: "/helper-tasks",     icon: ClipboardCheck, label: "Work Orders",         perm: "helper_tasks.manage" },
      { to: "/worksheet",        icon: ClipboardList,  label: "Daily Worksheet",     perm: "worksheet.view" },
      { to: "/staff",            icon: HardHat,        label: "Staff & Payroll",     perm: "staff.view" },
    ],
  },
  {
    key: "finance", label: "Finance & Reports",
    items: [
      { to: "/invoices",          icon: ReceiptText, label: "Invoices",          perm: "invoices.view" },
      { to: "/finance",           icon: Wallet,      label: "Finance",           perm: "finance.view" },
      { to: "/expenses",          icon: Receipt,     label: "Expenses",          perm: "expenses.view" },
      { to: "/forecast",          icon: TrendingUp,  label: "Forecast",          perm: "forecast.view" },
      { to: "/housing-analytics", icon: BarChart3,   label: "Housing Analytics", perm: "analytics.view" },
    ],
  },
  {
    key: "admin", label: "Administration",
    items: [
      { to: "/users",            icon: UsersRound,  label: "User Management",     perm: "users.view" },
      { to: "/departments",      icon: Building2,   label: "Departments",         perm: "departments.view" },
      { to: "/roles",            icon: ShieldAlert, label: "Roles & Permissions", perm: "roles.manage" },
      { to: "/approval-routing", icon: Workflow,    label: "Approval Routing",    perm: "approval_routing.manage" },
      { to: "/email-templates",  icon: Mail,        label: "Email Templates",     perm: "email_templates.manage" },
      { to: "/surveys",          icon: ListChecks,  label: "Surveys",             perm: "surveys.manage" },
      { to: "/stakeholders",     icon: Users,       label: "Stakeholders",        perm: "stakeholders.manage" },
      { to: "/contacts",         icon: Contact,     label: "Contacts",            perm: "contacts.view" },
      { to: "/org-settings",     icon: Settings2,   label: "Org Settings",        perm: "org_settings.update" },
      { to: "/audit-log",        icon: ScrollText,  label: "Audit Log",           perm: "audit.view" },
    ],
  },
];

const BOTTOM_ITEMS = [
  { to: "/profile", icon: UserCircle, label: "Profile" },
];

const OPEN_GROUPS_KEY = "sidebar_open_groups";

function NavItem({ item, onNav }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNav}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-all duration-200 ${
          isActive ? "bg-white/15 text-white font-medium" : "text-white/60 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      {item.label}
    </NavLink>
  );
}

export function Sidebar({ onClose }) {
  const { user, organization, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  const handleNav = () => { if (onClose) onClose(); };

  const isTenantLinked = user.role === "tenant" || (Array.isArray(user.tenant_lease_ids) && user.tenant_lease_ids.length > 0);

  const canSee = (item) => {
    if (item.globalAdminOnly) return user.role === "global_admin";
    if (!item.perm && !item.anyPerm) return true;
    if (item.perm && hasPerm(item.perm)) return true;
    if (item.anyPerm && item.anyPerm.some((p) => hasPerm(p))) return true;
    return false;
  };

  const visibleGroups = NAV_GROUPS
    .filter((g) => !g.tenantOnly || isTenantLinked)
    .map((g) => ({ ...g, items: g.items.filter(canSee) }))
    .filter((g) => g.items.length > 0);

  const activeGroupKey = visibleGroups.find((g) =>
    g.items.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + "/"))
  )?.key;

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY));
      if (Array.isArray(saved)) return saved;
    } catch { /* ignore */ }
    return null; // null = not initialised yet
  });

  // First run (or when active route changes group): make sure the active group is open.
  useEffect(() => {
    setOpenGroups((prev) => {
      const base = prev === null ? (visibleGroups[0] ? [visibleGroups[0].key] : []) : prev;
      if (activeGroupKey && !base.includes(activeGroupKey)) return [...base, activeGroupKey];
      return base;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  useEffect(() => {
    if (openGroups !== null) localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const base = prev || [];
      return base.includes(key) ? base.filter((k) => k !== key) : [...base, key];
    });
  };

  const title = user.role === "global_admin"
    ? "Mission SaaS"
    : (organization?.display_name?.split(" ")[0] || "Mission Hospitality");
  const subtitle = user.role === "global_admin"
    ? "Super-admin console"
    : (organization?.display_name || "Mission Hospitality");

  const open = openGroups || [];

  return (
    <div className="w-64 bg-[#1B4332] text-white flex flex-col h-full shrink-0" data-testid="sidebar">
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center shrink-0">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="sidebar-org-title">{title}</h1>
            <p className="text-xs text-white/50 leading-tight truncate" data-testid="sidebar-org-subtitle">{subtitle}</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1 hover:bg-white/10 rounded" data-testid="close-sidebar-btn">
            <X className="h-5 w-5 text-white/60" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {TOP_ITEMS.filter(canSee).map((item) => (
          <NavItem key={item.to} item={item} onNav={handleNav} />
        ))}

        {visibleGroups.map((group) => {
          const isOpen = open.includes(group.key);
          const hasActive = group.key === activeGroupKey;
          return (
            <div key={group.key} className="pt-1" data-testid={`sidebar-group-${group.key}`}>
              <button
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                  hasActive ? "text-white/80" : "text-white/40 hover:text-white/70"
                }`}
                data-testid={`sidebar-group-toggle-${group.key}`}
              >
                {group.label}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
              </button>
              {isOpen && (
                <div className="space-y-0.5 mt-0.5">
                  {group.items.map((item) => (
                    <NavItem key={item.to} item={item} onNav={handleNav} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="pt-1">
          {BOTTOM_ITEMS.map((item) => (
            <NavItem key={item.to} item={item} onNav={handleNav} />
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="mb-3">
          <p className="text-sm text-white/80 font-medium truncate">{user.name}</p>
          <p className="text-xs text-white/40 truncate">{user.email}</p>
          {user.department && (
            <p className="text-xs text-white/30 mt-0.5">{user.department} Dept</p>
          )}
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors duration-200 w-full"
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </div>
    </div>
  );
}
